import React,{ useState } from "react";
import Card from "components/card";
import Button from "components/button";
import AdvanceCandidate from "./AdvanceCandidate";
import {
    Mail,
    Phone,
    MapPin,
    CheckCircle,
    Sparkles,
    GraduationCap,
} from "lucide-react";

const CandidateProfile = ({ candidate, onAdvance }) => {

    const [isOpen, setIsOpen] = useState(false);
        const handleConfirm = () => {
        onAdvance();      
        setIsOpen(false);  
    };
    
    if (!candidate) return null;

    const {
        name,
        role,
        location,
        email,
        phone: phoneNumber,
        experience,
        education,
        applied,
        skills = [],
        notes,
    } = candidate;

    const initials = name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();

    return (
        <Card className="flex flex-col gap-6 rounded-2xl border-2 border-emerald-500 shadow-xl bg-white lg:sticky lg:top-6 overflow-visible">
            <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 border-b border-slate-200 rounded-t-2xl p-4 sm:p-5 flex items-center gap-3">
                <span className="relative flex w-14 h-14 shrink-0 overflow-hidden rounded-full border-2 border-white shadow-md bg-gradient-to-br from-emerald-500 to-cyan-500">
                    <span className="flex size-full items-center justify-center font-semibold text-[16px] sm:text-[18px] text-white">
                        {initials}
                    </span>
                </span>
                <div className="min-w-0 flex-1">
                    <h4 className="text-[18px] truncate">{name}</h4>
                    <div className="flex items-center gap-2 mt-1">
                        <p className="text-slate-500 text-[13px] truncate">{role}</p>
                    </div>
                </div>
            </div>

            <div className="p-4 sm:p-6 space-y-5">
                <section>
                    <h4 className="font-semibold text-[13px] flex items-center gap-2 mb-2 text-gray-800">
                        <Mail className="w-4 h-4 text-emerald-600" /> Contact Information
                    </h4>
                    <div className="space-y-1 text-[12px] text-gray-700">
                        <div className="flex items-center gap-2">
                            <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <a href={`mailto:${email}`} className="text-emerald-600 truncate hover:underline">
                                {email}
                            </a>
                        </div>
                        <div className="flex items-center gap-2">
                            <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-800">{phoneNumber}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-800">{location}</span>
                        </div>
                    </div>
                </section>

                <hr className="border-t border-gray-200" />

                <section>
                    <h4 className="font-semibold text-[13px] flex items-center gap-2 mb-2 text-gray-800">
                        <GraduationCap className="w-4 h-4 text-emerald-600" /> Background
                    </h4>
                    <div className="text-[12px] space-y-1">
                        <div>
                            <p className="text-gray-500 text-[11px]">Experience</p>
                            <p className="font-medium">{experience} years</p>
                        </div>
                        <div>
                            <p className="text-gray-500 text-[11px]">Education</p>
                            <p className="font-medium">{education}</p>
                        </div>
                        <div>
                            <p className="text-gray-500 text-[11px]">Applied Date</p>
                            <p className="font-medium">{applied}</p>
                        </div>
                    </div>
                </section>

                <hr className="border-t border-gray-200" />

                <section>
                    <h4 className="font-semibold text-[13px] mb-2 flex items-center gap-2 text-gray-800">
                        <Sparkles className="w-4 h-4 text-emerald-600" />
                        <span>Skills</span>
                    </h4>
                    <div className="flex flex-wrap gap-2">
                        {skills.map((skill) => (
                            <span
                                key={skill}
                                className="inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-medium whitespace-nowrap hover:bg-emerald-200 transition"
                            >
                                {skill}
                            </span>
                        ))}
                    </div>
                </section>

                <hr className="border-t border-gray-200" />

                <section>
                    <h4 className="font-semibold text-[13px] mb-2">Notes</h4>
                    <p className="text-[12px] bg-slate-50 border border-slate-200 rounded-lg p-3 text-gray-700 leading-relaxed">
                        {notes}
                    </p>
                </section>

                <div className="space-y-2 pt-2">
                    <Button 
                        onClick={() => setIsOpen(true)}
                        className="w-full h-9 flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-cyan-600 text-white font-medium rounded-lg py-2 text-[13px] sm:text-[14px] hover:from-emerald-700 hover:to-cyan-700 transition"
                    >
                        <CheckCircle className="w-3 h-3" />
                        <span>Advance to Next Stage</span>
                    </Button>
                    
                    <Button className="w-full h-9 flex items-center justify-center gap-2 border border-gray-300 rounded-lg py-2 text-[13px] text-gray-800 font-medium hover:bg-gray-50 transition">
                        <Mail className="w-3 h-3" /> 
                        <span>Contact Candidate</span>
                    </Button>

                    <AdvanceCandidate
                        candidateName={name}
                        isOpen={isOpen}
                        onClose={() => setIsOpen(false)}
                        onConfirm={handleConfirm}
                    />
                </div>
            </div>
        </Card>
    );
};

export default CandidateProfile;
